/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON call settings (decision 80, Figma "08 · Calls" 11–13): texts and the
 * settings Element Call does not have. All the wording is in this file.
 */

import { combineLatest, map, type Observable } from "rxjs";

import { Setting } from "../settings/settings";
import { type Behavior } from "../state/Behavior";
import { type ObservableScope } from "../state/ObservableScope";
import { type IonText } from "./callErrorTexts";

/** iON call settings in the settings window; false brings back upstream's. */
export const ION_CALL_SETTINGS: boolean = true;

/** Mirror my own front-facing camera (as Element Call always did). */
export const ionMirrorSelf = new Setting<boolean>("ion-mirror-self", true);

/** Show participants' names on the tiles of group calls. */
export const ionShowNames = new Setting<boolean>("ion-show-names", true);

export const CALL_SETTINGS_TEXTS = {
  title: { ru: "Настройки звонка", en: "Call settings" },
  audioTab: { ru: "Аудио", en: "Audio" },
  videoTab: { ru: "Видео", en: "Video" },
  generalTab: { ru: "Настройки", en: "Settings" },
  changeAudioDevice: {
    ru: "Изменить аудиоустройство",
    en: "Change audio device",
  },
  soundOutput: { ru: "Вывод звука", en: "Sound output" },
  microphone: { ru: "Микрофон", en: "Microphone" },
  effectsVolume: {
    ru: "Громкость звуковых эффектов",
    en: "Sound effects volume",
  },
  effectsVolumeDescription: {
    ru: "Громкость звуков реакций и поднятой руки.",
    en: "Volume of the reaction and raised hand sounds.",
  },
  camera: { ru: "Камера", en: "Camera" },
  image: { ru: "Изображение", en: "Image" },
  mirror: { ru: "Зеркалить моё видео", en: "Mirror my video" },
  blur: { ru: "Размытие фона", en: "Background blur" },
  blurDescription: {
    ru: "Скрывает обстановку вокруг вас",
    en: "Hides your surroundings",
  },
  blurUnsupported: {
    ru: "Недоступно на этом устройстве",
    en: "Not available on this device",
  },
  noiseSuppression: { ru: "Шумоподавление", en: "Noise suppression" },
  noiseSuppressionDescription: {
    ru: "Убирает фоновый шум микрофона",
    en: "Removes background noise from your microphone",
  },
  dataSaver: { ru: "Экономия трафика", en: "Data saver" },
  dataSaverDescription: {
    ru: "Снижает качество видео в звонке",
    en: "Lowers the video quality of calls",
  },
  showNames: {
    ru: "Показывать имена участников",
    en: "Show participant names",
  },
  showNamesDescription: {
    ru: "Только в групповых звонках",
    en: "Group calls only",
  },
} satisfies Record<string, IonText>;

/**
 * Name tags with the iON setting: switched off, a group call (more than
 * two room members) shows no names; a call of two keeps upstream's rule.
 */
export function withIonNameTags(
  scope: ObservableScope,
  showNameTags$: Behavior<boolean>,
  members$: Observable<{ size: number }>,
): Behavior<boolean> {
  return scope.behavior(
    combineLatest([showNameTags$, ionShowNames.value$, members$]).pipe(
      map(
        ([upstream, setting, members]) =>
          upstream && (setting || members.size <= 2),
      ),
    ),
  );
}
